/**
 * Assembles the inputs for the world-class CFO forecast workbook.
 *
 * The detailed three-way statements are always monthly (the only grain at which
 * the server produces P&L + Balance Sheet + Cash Flow). This orchestrator fans
 * out `getQuickBooksFpaMlStatementForecast` across {P&L, Balance Sheet, Cash Flow}
 * × {High, Expected, Low}, aligns the three value bands per statement row, derives
 * the editable drivers from the ML driver assumptions, and seeds the live driver
 * model from the expected forecast. It degrades gracefully: any statement/case
 * that fails to load is skipped, and a `currentStatement` fallback keeps the
 * export working offline-of-some-data.
 */
import type {
  QuickBooksFpaMlForecastVsActualRow,
  QuickBooksFpaMlStatementForecastResponse,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
} from '../../../data/ServerApiClient'
import {
  CFO_DRIVER_DEFS,
  clampDriver,
  type CfoCase,
  type CfoDriverData,
} from './cfoForecastDriverModel'
import { mergeActualAndForecastStatements } from './forecastStatementAssembly'
import { closedActualMonthCount, historicalDriverMetrics, representativeRecentActualValue } from './forecastDriverEngine'
import type {
  CfoForecastWorkbookInput,
  CfoStatementData,
  CfoStatementRowData,
  CfoStatementType,
} from './cfoForecastWorkbookModel'

/** Minimal surface of ServerApiClient this module needs (keeps it decoupled + testable). */
export type ForecastStatementApi = {
  getQuickBooksFpaMlStatementForecast(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      grain?: string | null
      statement_type?: string | null
      value_case?: string | null
      as_of_date?: string | null
      future_months?: number | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlStatementForecastResponse>
  /** Render actual (historical) statements. Optional — when absent the export is forecast-only. */
  renderQuickBooksFpaStatement?(req: {
    connection_id: string
    layout_version_id?: string | null
    statement_type?: string | null
    start_date?: string | null
    end_date?: string | null
    months?: number | null
    workbook_id?: string | null
  }): Promise<QuickBooksFpaStatementRender>
}

export type CfoForecastExportRequest = {
  api: ForecastStatementApi
  workbookId: string
  companyName: string
  anchorPeriod: string | null
  scenarioKey?: string | null
  packageId?: string | null
  futureMonths?: number | null
  currencySymbol?: string
  generatedAtIso?: string
  /** ML driver assumptions (`getQuickBooksFpaMlDriverAssumptions().assumptions`). */
  assumptions?: unknown
  /** Baseline (un-edited) driver assumptions, for the deviation highlight. */
  baselineAssumptions?: unknown
  /** Fallback statement already loaded on the page, used if the fan-out fails. */
  currentStatement?: QuickBooksFpaStatementRender | null
  currentStatementType?: CfoStatementType | null
  /** QBO connection + layout for fetching actual history (enables actuals-vs-forecast). */
  connectionId?: string | null
  layoutVersionId?: string | null
  /** Months of actual history to include before the forecast (default 24). */
  historyMonths?: number | null
  /**
   * The page's online direct-cash forecast, rolled forward, per value case. When
   * supplied, the export's forecast ending-cash bands come from this (matching the
   * online model) instead of the wider Balance-Sheet cash-balance prediction. Each
   * statement must carry an ending-cash row.
   */
  forecastCashByCase?: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null
  /**
   * Page-harvested TIMING-AWARE weekly/daily direct cash, per value case (dated AR/AP
   * + recurring payroll/rent/loan schedules). When supplied, the 13-week/Daily sheets
   * use these instead of an even allocation. Each carries the standard
   * `fg-forecast-cash-*` rows. Falls back to the even spread when absent (e.g. headless).
   */
  weeklyCashByCase?: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null
  dailyCashByCase?: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null
  /** Backtest rows for the Accuracy sheet's historical block (forecast-vs-actual). */
  forecastVsActualRows?: QuickBooksFpaMlForecastVsActualRow[] | null
  /** Data-readiness status + basis labels (honesty gating). */
  readinessStatus?: string | null
  basisLabels?: string[] | null
  /** QB sync / last closed actual month, and how many closed months feed the model. */
  dataAsOf?: string | null
  closedMonthsCount?: number | null
  /**
   * Hide unused detail rows (zero across every case+period) and inactive "(deleted)"
   * accounts, mirroring the studio's "Hide blank rows" setting. Defaults to true so the
   * one-click export is clean; totals/subtotals/group headers are always retained.
   */
  hideUnusedRows?: boolean
}

const STATEMENT_TYPES: Array<{ type: CfoStatementType; label: string }> = [
  { type: 'profit_and_loss', label: 'Income Statement' },
  { type: 'balance_sheet', label: 'Balance Sheet' },
  { type: 'cash_flow', label: 'Cash Flow' },
]

const CASES: CfoCase[] = ['high', 'expected', 'low']

function monthKey(period: string | null | undefined): string {
  return String(period ?? '').slice(0, 7)
}

function derivePeriodKinds(periods: string[], anchorPeriod: string | null): Array<'actual' | 'forecast'> {
  const anchor = monthKey(anchorPeriod)
  if (!anchor) return periods.map(() => 'forecast')
  return periods.map((p) => (monthKey(p) <= anchor ? 'actual' : 'forecast'))
}

/**
 * True when a row carries no number in any case/period (within rounding). Mirrors the
 * studio's `rowHasStatementValue` 0.005 tolerance, on the multi-case `byCase` shape.
 */
function rowIsBlank(row: CfoStatementRowData): boolean {
  return !CASES.some((c) => row.byCase[c].some((v) => v != null && Math.abs(Number(v)) > 0.005))
}

/**
 * Whether to keep a row when hiding unused rows. Always retains structural rows
 * (totals/subtotals/group headers/formulas) so the hierarchy stays intact, drops
 * QBO "(deleted)" inactive accounts outright, and drops zero leaf rows — matching the
 * studio's "Hide blank rows" filter so the export reflects what the user sees online.
 */
function keepRowWhenHidingUnused(row: CfoStatementRowData): boolean {
  if (/\(deleted\)/i.test(row.label)) return false
  if (row.isTotal || row.isFormula || row.rowType === 'heading' || row.rowType === 'group') return true
  return !rowIsBlank(row)
}

function rowByCase(
  byType: Record<CfoCase, QuickBooksFpaStatementRender | null>,
  periodCount: number,
  hideUnusedRows = true,
): CfoStatementRowData[] {
  const expected = byType.expected
  if (!expected) return []
  const indexByNode = (render: QuickBooksFpaStatementRender | null): Map<string, QuickBooksFpaStatementRow> => {
    const map = new Map<string, QuickBooksFpaStatementRow>()
    for (const row of render?.rows ?? []) map.set(row.node_id, row)
    return map
  }
  const highIdx = indexByNode(byType.high)
  const lowIdx = indexByNode(byType.low)
  const fit = (values: Array<number | null> | undefined): Array<number | null> => {
    const out = new Array<number | null>(periodCount).fill(null)
    ;(values ?? []).forEach((v, i) => { if (i < periodCount) out[i] = typeof v === 'number' && Number.isFinite(v) ? v : null })
    return out
  }
  const mapped = expected.rows.map((row): CfoStatementRowData => {
    const high = highIdx.get(row.node_id)
    const low = lowIdx.get(row.node_id)
    return {
      nodeId: row.node_id,
      parentNodeId: row.parent_node_id ?? null,
      label: row.label || row.node_id,
      level: Number(row.level || 0),
      rowType: row.row_type || 'line',
      isTotal: Boolean(row.is_total),
      isFormula: Boolean(row.is_formula),
      byCase: {
        expected: fit(row.values),
        high: fit((high ?? row).values),
        low: fit((low ?? row).values),
      },
    }
  })
  return hideUnusedRows ? mapped.filter(keepRowWhenHidingUnused) : mapped
}

// ---------------------------------------------------------------------------
// Cash-flow derivation (the ML serves a populated P&L + Balance Sheet, but the
// monthly direct-cash statement arrives all-null off the server endpoint — the
// app fills it client-side via DuckDB. Rather than replicate that pipeline, we
// derive a reconciling INDIRECT cash flow from the populated statements: ending
// cash is read straight off the Balance Sheet cash line (so CF ties to BS), and
// operating cash is net income plus the working-capital plug.)
// ---------------------------------------------------------------------------

function emptyByCase(n: number): Record<CfoCase, Array<number | null>> {
  return { high: new Array(n).fill(null), expected: new Array(n).fill(null), low: new Array(n).fill(null) }
}

/** byCase series of the best row matching `match` (prefer totals), or null. */
function findRowByCaseData(
  data: CfoStatementData,
  match: RegExp,
  opts?: { preferTotal?: boolean },
): Record<CfoCase, Array<number | null>> | null {
  const candidates = data.rows.filter((r) => match.test(r.label || ''))
  if (candidates.length === 0) return null
  const ordered = opts?.preferTotal ? [...candidates].sort((a, b) => Number(b.isTotal) - Number(a.isTotal)) : candidates
  return ordered[0].byCase
}

const fin = (v: number | null | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** The direct-cash operating detail nodes (receipts/disbursements breakdown). */
const DIRECT_CASH_DETAIL_NODES: Record<string, number> = {
  'fg-forecast-cash-receipts': 1,
  'fg-forecast-cash-customer-receipts': 2,
  'fg-forecast-cash-other-receipts': 2,
  'fg-forecast-cash-disbursements': 1,
  'fg-forecast-cash-payroll': 2,
  'fg-forecast-cash-vendors': 2,
  'fg-forecast-cash-debt-tax-capex': 2,
  'fg-forecast-cash-other-disbursements': 2,
}

/**
 * Build the rich direct-cash operating detail (receipts/disbursements breakdown) from
 * the page-harvested monthly direct cash, aligned to the statement's period axis (values
 * land on the matching forecast month, actuals stay null). Returns [] when no rich harvest
 * is available (e.g. headless), so the Cash Flow keeps its reconciling summary only.
 */
function harvestedDirectCashDetailRows(
  byCase: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null | undefined,
  periods: string[],
): CfoStatementRowData[] {
  const expected = byCase?.expected
  if (!expected) return []
  const bsIdx = new Map<string, number>()
  periods.forEach((p, i) => bsIdx.set(monthKey(p), i))
  const renders: Record<CfoCase, QuickBooksFpaStatementRender | null> = {
    high: byCase?.high ?? expected, expected, low: byCase?.low ?? expected,
  }
  const aligned = (render: QuickBooksFpaStatementRender | null, nodeId: string): Array<number | null> => {
    const out = new Array<number | null>(periods.length).fill(null)
    const row = render?.rows.find((r) => r.node_id === nodeId)
    if (!row || !render) return out
    render.periods.forEach((p, i) => {
      const bi = bsIdx.get(monthKey(p))
      if (bi != null) { const v = row.values?.[i]; out[bi] = v != null && Number.isFinite(Number(v)) ? Number(v) : null }
    })
    return out
  }
  const rows: CfoStatementRowData[] = []
  for (const row of expected.rows) {
    const level = DIRECT_CASH_DETAIL_NODES[row.node_id]
    if (level == null) continue
    const exp = aligned(renders.expected, row.node_id)
    if (!exp.some((v) => v != null && Math.abs(v) > 0.005)) continue
    rows.push({
      nodeId: `cfdetail-${row.node_id}`,
      parentNodeId: null,
      label: row.label || row.node_id,
      level,
      rowType: level === 1 ? 'group' : 'line',
      isTotal: level === 1,
      isFormula: false,
      byCase: { expected: exp, high: aligned(renders.high, row.node_id), low: aligned(renders.low, row.node_id) },
    })
  }
  return rows
}

/**
 * Build a reconciling indirect cash-flow `CfoStatementData` from the assembled
 * P&L + Balance Sheet. Returns null if no Balance Sheet cash line is found (the
 * caller then keeps the server/current-statement cash fallback). When the page
 * harvested the rich monthly direct cash, its receipts/disbursements detail is
 * appended as a supplementary section so the export matches the studio's depth
 * while the summary above keeps tying to the Balance Sheet.
 */
export function deriveCashFlowStatement(
  pl: CfoStatementData | undefined,
  bs: CfoStatementData | undefined,
  openingCash: number,
  directEndingByCase?: Record<CfoCase, Map<string, number>> | null,
  richDetailByCase?: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null,
): CfoStatementData | null {
  if (!bs || bs.periods.length === 0) return null
  const bsCash = findRowByCaseData(bs, /(total\s+)?(bank|cash and cash|cash)\b/i, { preferTotal: true })
    ?? findRowByCaseData(bs, /cash/i, { preferTotal: true })
  if (!bsCash) return null
  const plNet = pl
    ? (findRowByCaseData(pl, /net\s+(income|profit|earnings|loss)/i, { preferTotal: true })
      ?? findRowByCaseData(pl, /net\s+income/i))
    : null
  const n = bs.periods.length
  const ending = emptyByCase(n)
  const beginning = emptyByCase(n)
  const netChange = emptyByCase(n)
  const netIncome = emptyByCase(n)
  const adjustments = emptyByCase(n)
  // Footing guard: the online direct-cash model can run away when the server publishes
  // degenerate drivers (e.g. 100% gross margin ⇒ no modelled disbursements), compounding
  // receipts into an implausible balance that no longer ties to the Balance-Sheet cash
  // line. When the direct ending cash diverges wildly from BS cash at the last forecast
  // period, reject it and fall back to the BS cash line so the statement always foots
  // (CF ending == BS cash). Plausible direct cash — the normal case (e.g. CFO Core, where
  // the two already agree) — is used exactly as before.
  const directIsPlausible = ((): boolean => {
    const direct = directEndingByCase?.expected
    if (!direct) return false
    let lastForecastIdx = -1
    for (let i = 0; i < n; i += 1) if (bs.periodKinds[i] === 'forecast') lastForecastIdx = i
    if (lastForecastIdx < 0) return true
    const d = fin(direct.get(monthKey(bs.periods[lastForecastIdx])))
    if (d == null) return false
    const b = fin(bsCash.expected[lastForecastIdx])
    if (b == null) return true
    const anchor = Number.isFinite(openingCash) ? Math.abs(openingCash) : 0
    const scale = Math.max(Math.abs(b), anchor, 1)
    return Math.abs(d - b) <= 5 * scale
  })()
  for (const c of CASES) {
    // Ending cash: for FORECAST periods, prefer the harvested ONLINE displayed cash
    // (the page's driver-modeled, anchor-rolled statement — bounded and correctly
    // oriented, matching what the CFO sees online). Fall back to the Balance-Sheet
    // cash line. Actual periods always use the real BS cash.
    const direct = directIsPlausible ? directEndingByCase?.[c] : null
    const raw = bs.periods.map((p, i) => {
      if (direct && bs.periodKinds[i] === 'forecast') {
        const v = fin(direct.get(monthKey(p)))
        if (v != null) return v
      }
      return fin(bsCash[c][i])
    })
    let prevEnd: number | null = Number.isFinite(openingCash) ? openingCash : null
    for (let i = 0; i < n; i += 1) {
      const end = raw[i]
      const beg = i === 0 ? (Number.isFinite(openingCash) ? openingCash : end) : (raw[i - 1] ?? prevEnd)
      const chg = end != null && beg != null ? end - beg : null
      const ni = plNet ? fin(plNet[c][i]) : null
      const adj = chg != null && ni != null ? chg - ni : null
      ending[c][i] = end
      beginning[c][i] = beg
      netChange[c][i] = chg
      netIncome[c][i] = ni
      adjustments[c][i] = adj
      if (end != null) prevEnd = end
    }
  }
  // Orient ending/beginning/netChange per period so High >= Low (more cash is better).
  for (let i = 0; i < n; i += 1) {
    const lo = fin(ending.low[i]); const hi = fin(ending.high[i])
    if (lo != null && hi != null && lo > hi) {
      ;[ending.low[i], ending.high[i]] = [hi, lo]
      ;[beginning.low[i], beginning.high[i]] = [beginning.high[i], beginning.low[i]]
      ;[netChange.low[i], netChange.high[i]] = [netChange.high[i], netChange.low[i]]
    }
  }
  const hasNet = CASES.some((c) => netIncome[c].some((v) => v != null))
  const rows: CfoStatementRowData[] = []
  if (hasNet) {
    rows.push({ nodeId: 'cf-op', parentNodeId: null, label: 'Operating activities', level: 0, rowType: 'group', isTotal: false, isFormula: false, byCase: emptyByCase(n) })
    rows.push({ nodeId: 'cf-ni', parentNodeId: 'cf-op', label: 'Net income', level: 1, rowType: 'line', isTotal: false, isFormula: false, byCase: netIncome })
    rows.push({ nodeId: 'cf-wc', parentNodeId: 'cf-op', label: 'Working capital & non-cash adjustments', level: 1, rowType: 'line', isTotal: false, isFormula: false, byCase: adjustments })
  }
  rows.push({ nodeId: 'cf-net', parentNodeId: null, label: 'Net change in cash', level: 0, rowType: 'total', isTotal: true, isFormula: true, byCase: netChange })
  rows.push({ nodeId: 'cf-begin', parentNodeId: null, label: 'Beginning cash', level: 0, rowType: 'line', isTotal: false, isFormula: false, byCase: beginning })
  rows.push({ nodeId: 'cf-end', parentNodeId: null, label: 'Ending cash', level: 0, rowType: 'total', isTotal: true, isFormula: true, byCase: ending })
  // Supplementary direct-cash detail (matches the studio's depth): receipts and
  // disbursements broken out for the forecast months. The reconciling summary above
  // remains the authoritative, BS-tied cash; this section is the operating breakdown.
  const detailRows = harvestedDirectCashDetailRows(richDetailByCase, bs.periods)
  if (detailRows.length > 0) {
    rows.push({ nodeId: 'cf-detail', parentNodeId: null, label: 'Forecast operating cash detail (direct view)', level: 0, rowType: 'heading', isTotal: false, isFormula: false, byCase: emptyByCase(n) })
    rows.push(...detailRows)
  }
  return { statementType: 'cash_flow', label: 'Cash Flow', periods: bs.periods, periodKinds: bs.periodKinds, rows }
}

/** Map each case's online direct-cash ending-cash trajectory to monthKey -> value. */
function extractDirectEndingByCase(
  byCase: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null | undefined,
): Record<CfoCase, Map<string, number>> | null {
  if (!byCase) return null
  const endingRow = (s: QuickBooksFpaStatementRender | null | undefined) =>
    s?.rows.find((r) => r.node_id === 'fg-forecast-cash-ending-cash')
    ?? s?.rows.find((r) => /ending|closing/i.test(r.label || '') && /cash|balance/i.test(r.label || ''))
  const buildMap = (s: QuickBooksFpaStatementRender | null | undefined): Map<string, number> => {
    const map = new Map<string, number>()
    const row = endingRow(s)
    if (!row || !s) return map
    s.periods.forEach((p, i) => {
      // Guard null/undefined before Number(): Number(null) === 0 would record an
      // empty forecast ending cell as a real $0 and override the BS cash fallback.
      const raw = row.values?.[i]
      const v = raw == null ? NaN : Number(raw)
      if (Number.isFinite(v)) map.set(monthKey(p), v)
    })
    return map
  }
  const exp = buildMap(byCase.expected)
  if (exp.size === 0) return null
  return {
    expected: exp,
    high: byCase.high ? buildMap(byCase.high) : exp,
    low: byCase.low ? buildMap(byCase.low) : exp,
  }
}

/**
 * Allocate a monthly derived cash flow onto a sub-monthly (weekly/daily) period
 * axis by spreading each month's net change evenly across its sub-periods and
 * rolling the balance forward. Keeps the liquidity views consistent with the
 * monthly cash flow when the server serves only the period axis (no values).
 */
export function allocateSubMonthlyCash(
  monthly: CfoStatementData | null,
  grain: CfoStatementData | null,
  openingCash: number,
): CfoStatementData | null {
  if (!monthly || !grain || grain.periods.length === 0) return null
  const netRow = monthly.rows.find((r) => /net\s+(change|cash)/i.test(r.label))
  if (!netRow) return null
  const monthKeyOf = (p: string) => String(p).slice(0, 7)
  const monthNet: Record<CfoCase, Map<string, number>> = { high: new Map(), expected: new Map(), low: new Map() }
  monthly.periods.forEach((p, i) => {
    const key = monthKeyOf(p)
    for (const c of CASES) monthNet[c].set(key, fin(netRow.byCase[c][i]) ?? 0)
  })
  // Count sub-periods per month so we can split each month's net evenly.
  const countByMonth = new Map<string, number>()
  for (const p of grain.periods) countByMonth.set(monthKeyOf(p), (countByMonth.get(monthKeyOf(p)) ?? 0) + 1)

  const n = grain.periods.length
  const ending = emptyByCase(n)
  const beginning = emptyByCase(n)
  const netChange = emptyByCase(n)
  for (const c of CASES) {
    let prev = Number.isFinite(openingCash) ? openingCash : 0
    grain.periods.forEach((p, i) => {
      const key = monthKeyOf(p)
      const count = Math.max(1, countByMonth.get(key) ?? 1)
      const chg = (monthNet[c].get(key) ?? 0) / count
      const beg = prev
      const end = beg + chg
      beginning[c][i] = beg
      netChange[c][i] = chg
      ending[c][i] = end
      prev = end
    })
  }
  const rows: CfoStatementRowData[] = [
    { nodeId: 'cf-sub-net', parentNodeId: null, label: 'Net cash change', level: 0, rowType: 'total', isTotal: true, isFormula: true, byCase: netChange },
    { nodeId: 'cf-sub-begin', parentNodeId: null, label: 'Beginning cash', level: 0, rowType: 'line', isTotal: false, isFormula: false, byCase: beginning },
    { nodeId: 'cf-sub-end', parentNodeId: null, label: 'Ending cash', level: 0, rowType: 'total', isTotal: true, isFormula: true, byCase: ending },
  ]
  return { statementType: 'cash_flow', label: grain.label, periods: grain.periods, periodKinds: grain.periodKinds, rows }
}

// ---------------------------------------------------------------------------
// Driver assumption normalisation
// ---------------------------------------------------------------------------

function pick(obj: unknown, key: string): unknown {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) return (obj as Record<string, unknown>)[key]
  return undefined
}

function asNumber(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Pull expected/low/high (in driver-native units) from a loosely-typed assumption value. */
function readTriplet(raw: unknown): { expected: number | null; low: number | null; high: number | null } {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      expected: asNumber(pick(raw, 'expected')) ?? asNumber(pick(raw, 'value')) ?? asNumber(pick(raw, 'base')),
      low: asNumber(pick(raw, 'low')),
      high: asNumber(pick(raw, 'high')),
    }
  }
  const n = asNumber(raw)
  return { expected: n, low: null, high: null }
}

/** Percent drivers may arrive as points (15) or fractions (0.15); normalise to fractions. */
function normalizePercent(value: number, unit: string): number {
  if (unit !== 'percent') return value
  return Math.abs(value) > 1.5 ? value / 100 : value
}

/** Provenance per driver key, threaded into the Drivers sheet's "Your actual" column. */
export type DriverProvenanceEntry = {
  historicalActual: number | null
  sourceLabel: string | null
  provenanceNote: string | null
  sparseHistory: boolean
}
export type DriverProvenanceMap = Record<string, DriverProvenanceEntry>

const formatDriverActual = (def: { unit: string }, value: number): string => {
  if (def.unit === 'percent') return `${(value * 100).toFixed(1)}%`
  if (def.unit === 'days') return `${Math.round(value)} days`
  return value.toFixed(2)
}

/** Plain-language grounding note per driver, e.g. "DSO 47 days — 12-mo QuickBooks AR ÷ revenue." */
const DRIVER_PROVENANCE_BASIS: Record<string, string> = {
  sales_growth_pct: 'trailing-12-mo QuickBooks revenue growth',
  gross_margin_pct: '12-mo QuickBooks gross profit ÷ revenue',
  dso_days: '12-mo QuickBooks A/R ÷ revenue',
  dpo_days: '12-mo QuickBooks A/P ÷ COGS',
  opex_pct: '12-mo QuickBooks operating expense ÷ revenue',
}

/**
 * Builds the per-driver "your actual" provenance from the already-fetched actual
 * P&L + Balance Sheet, so the Drivers sheet can show "your actual = X" beside each
 * editable assumption. `seasonality_strength` has no historical metric → null.
 */
export function buildDriverProvenance(
  actualPl: QuickBooksFpaStatementRender | null,
  actualBs: QuickBooksFpaStatementRender | null,
): DriverProvenanceMap {
  const metrics = historicalDriverMetrics(actualPl, actualBs)
  const closedMonths = closedActualMonthCount(actualPl)
  const sparseHistory = closedMonths > 0 && closedMonths < 6
  const byDriver: Record<string, number | null> = {
    sales_growth_pct: metrics.revenueGrowth,
    gross_margin_pct: metrics.grossMargin,
    dso_days: metrics.dsoDays,
    dpo_days: metrics.dpoDays,
    opex_pct: metrics.opexPct,
    seasonality_strength: null,
  }
  const map: DriverProvenanceMap = {}
  for (const def of CFO_DRIVER_DEFS) {
    const raw = byDriver[def.key]
    const historicalActual = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
    const basis = DRIVER_PROVENANCE_BASIS[def.key]
    map[def.key] = historicalActual != null && basis
      ? {
        historicalActual,
        sourceLabel: 'Your QuickBooks history',
        provenanceNote: `${def.label.replace(/\s*\(.*\)\s*$/, '')} ${formatDriverActual(def, historicalActual)} — ${basis}. Editable for what-if.`,
        sparseHistory,
      }
      : { historicalActual: null, sourceLabel: null, provenanceNote: null, sparseHistory: false }
  }
  return map
}

export function normalizeDrivers(
  assumptions: unknown,
  baseline: unknown,
  provenance?: DriverProvenanceMap | null,
): CfoDriverData[] {
  const source = pick(assumptions, 'assumptions') ?? assumptions
  const baseSource = pick(baseline, 'assumptions') ?? baseline ?? source
  return CFO_DRIVER_DEFS.map((def) => {
    const triplet = readTriplet(pick(source, def.key))
    const baseTriplet = readTriplet(pick(baseSource, def.key))
    const norm = (v: number | null): number | null => (v == null ? null : normalizePercent(v, def.unit))
    const expected = clampDriver(def, norm(triplet.expected) ?? def.fallback)
    const low = clampDriver(def, norm(triplet.low) ?? expected)
    const high = clampDriver(def, norm(triplet.high) ?? expected)
    const baselineVal = clampDriver(def, norm(baseTriplet.expected) ?? expected)
    const prov = provenance?.[def.key]
    return {
      ...def,
      low,
      expected,
      high,
      baseline: baselineVal,
      historicalActual: prov?.historicalActual ?? null,
      sourceLabel: prov?.sourceLabel ?? null,
      provenanceNote: prov?.provenanceNote ?? null,
      sparseHistory: prov?.sparseHistory ?? false,
    }
  })
}

// ---------------------------------------------------------------------------
// Seed extraction (anchor the live driver model to the ML expected forecast)
// ---------------------------------------------------------------------------

function firstForecastIndex(periodKinds: Array<'actual' | 'forecast'>): number {
  const idx = periodKinds.indexOf('forecast')
  return idx >= 0 ? idx : Math.max(0, periodKinds.length - 1)
}

function findRowValue(
  render: QuickBooksFpaStatementRender | null,
  match: RegExp,
  periodIndex: number,
  opts?: { preferTotal?: boolean },
): number | null {
  if (!render) return null
  const candidates = render.rows.filter((r) => match.test(r.label || ''))
  const ordered = opts?.preferTotal
    ? [...candidates].sort((a, b) => Number(b.is_total) - Number(a.is_total))
    : candidates
  for (const row of ordered) {
    const v = row.values?.[periodIndex]
    if (typeof v === 'number' && Number.isFinite(v)) return v
  }
  return null
}

function maxRowValue(render: QuickBooksFpaStatementRender | null, periodIndex: number): number | null {
  if (!render) return null
  let best: number | null = null
  for (const row of render.rows) {
    const v = row.values?.[periodIndex]
    if (typeof v === 'number' && Number.isFinite(v) && (best == null || v > best)) best = v
  }
  return best
}

function derivePeriodKindsByDate(periods: string[], anchorPeriod: string | null): Array<'actual' | 'forecast'> {
  const anchor = String(anchorPeriod ?? '').slice(0, 10)
  if (!anchor) return periods.map(() => 'forecast')
  return periods.map((p) => (String(p).slice(0, 10) <= anchor ? 'actual' : 'forecast'))
}

/** Optional sub-monthly cash statement (weekly/daily) — gracefully null if unsupported. */
async function fetchGrainCash(
  request: CfoForecastExportRequest,
  grain: string,
  label: string,
): Promise<CfoStatementData | null> {
  const fetchCase = async (value_case: CfoCase): Promise<QuickBooksFpaStatementRender | null> => {
    try {
      const out = await request.api.getQuickBooksFpaMlStatementForecast(request.workbookId, {
        grain,
        statement_type: 'cash_flow',
        value_case,
        scenario_key: request.scenarioKey ?? undefined,
        package_id: request.packageId ?? undefined,
        as_of_date: request.anchorPeriod ?? undefined,
      })
      return out?.statement ?? null
    } catch {
      return null
    }
  }
  const [high, expected, low] = await Promise.all([fetchCase('high'), fetchCase('expected'), fetchCase('low')])
  if (!expected || !(expected.periods?.length)) return null
  const periods = expected.periods ?? []
  return {
    statementType: 'cash_flow',
    label,
    periods,
    periodKinds: derivePeriodKindsByDate(periods, request.anchorPeriod),
    // Sub-monthly cash keeps full timing detail (receipts/payroll/rent/debt rows) — not filtered.
    rows: rowByCase({ high, expected, low }, periods.length, false),
  }
}

/**
 * Convert a page-harvested timing-aware sub-monthly cash band (per value case) into the
 * CfoStatementData the workbook builders consume. Reuses rowByCase + derivePeriodKindsByDate,
 * exactly like fetchGrainCash — but the values are the real dated direct-cash, not null.
 */
function statementDataFromHarvestedCashByCase(
  byCase: Partial<Record<CfoCase, QuickBooksFpaStatementRender | null>> | null | undefined,
  label: string,
  anchorPeriod: string | null,
): CfoStatementData | null {
  const expected = byCase?.expected
  if (!expected || !(expected.periods?.length)) return null
  const periods = expected.periods
  return {
    statementType: 'cash_flow',
    label,
    periods,
    periodKinds: derivePeriodKindsByDate(periods, anchorPeriod),
    rows: rowByCase({ high: byCase?.high ?? expected, expected, low: byCase?.low ?? expected }, periods.length, false),
  }
}

// ---------------------------------------------------------------------------
// Top-level assembly
// ---------------------------------------------------------------------------

export async function assembleCfoForecastWorkbookInput(
  request: CfoForecastExportRequest,
): Promise<CfoForecastWorkbookInput> {
  const warnings: string[] = []
  const futureMonths = request.futureMonths && request.futureMonths > 0 ? request.futureMonths : 12

  const fetchOne = async (type: CfoStatementType, value_case: CfoCase): Promise<QuickBooksFpaStatementRender | null> => {
    try {
      const out = await request.api.getQuickBooksFpaMlStatementForecast(request.workbookId, {
        grain: 'month',
        statement_type: type,
        value_case,
        scenario_key: request.scenarioKey ?? undefined,
        package_id: request.packageId ?? undefined,
        as_of_date: request.anchorPeriod ?? undefined,
        future_months: futureMonths,
      })
      return out?.statement ?? null
    } catch {
      return null
    }
  }

  // Actual history (P&L + Balance Sheet) so the export shows actuals alongside the
  // forecast. Cash is derived from these, so it inherits the history too.
  const historyMonths = request.historyMonths && request.historyMonths > 0 ? request.historyMonths : 24
  const canFetchActuals = typeof request.api.renderQuickBooksFpaStatement === 'function' && !!request.connectionId
  const fetchActual = async (type: CfoStatementType): Promise<QuickBooksFpaStatementRender | null> => {
    if (!canFetchActuals) return null
    try {
      return await request.api.renderQuickBooksFpaStatement!({
        connection_id: request.connectionId!,
        layout_version_id: request.layoutVersionId ?? undefined,
        statement_type: type,
        end_date: request.anchorPeriod ?? undefined,
        months: historyMonths,
        workbook_id: request.workbookId,
      })
    } catch {
      return null
    }
  }

  // Fan out all statement × case combinations + the actuals in parallel.
  const [fetched, actualPl, actualBs] = await Promise.all([
    Promise.all(STATEMENT_TYPES.flatMap(({ type }) => CASES.map(async (c) => ({ type, c, render: await fetchOne(type, c) })))),
    fetchActual('profit_and_loss'),
    fetchActual('balance_sheet'),
  ])
  const byTypeCase = new Map<string, QuickBooksFpaStatementRender | null>()
  for (const { type, c, render } of fetched) byTypeCase.set(`${type}:${c}`, render)
  const actualByType: Partial<Record<CfoStatementType, QuickBooksFpaStatementRender | null>> = {
    profit_and_loss: actualPl,
    balance_sheet: actualBs,
  }
  let mergedHistory = false

  const statements: CfoStatementData[] = []
  for (const { type, label } of STATEMENT_TYPES) {
    const byCase: Record<CfoCase, QuickBooksFpaStatementRender | null> = {
      high: byTypeCase.get(`${type}:high`) ?? null,
      expected: byTypeCase.get(`${type}:expected`) ?? null,
      low: byTypeCase.get(`${type}:low`) ?? null,
    }
    // Fallback: use the page's already-loaded statement for the matching type.
    if (!byCase.expected && request.currentStatement && request.currentStatementType === type) {
      byCase.expected = request.currentStatement
      warnings.push(`${label}: live forecast fetch failed; used the statement currently shown in the app.`)
    }
    if (!byCase.expected) {
      warnings.push(`${label}: forecast data unavailable; sheet omitted.`)
      continue
    }

    // Merge actual history in front of each case's forecast (cash is derived later,
    // so it is not merged here). Actuals are identical across cases; only the
    // forecast tail diverges by High/Expected/Low.
    const actual = type === 'cash_flow' ? null : (actualByType[type] ?? null)
    let merged: Record<CfoCase, QuickBooksFpaStatementRender | null> | null = null
    if (actual && actual.rows?.length) {
      const m = (forecast: QuickBooksFpaStatementRender | null) =>
        forecast ? mergeActualAndForecastStatements(actual, forecast, historyMonths, futureMonths) : null
      const mergedExpected = m(byCase.expected)
      if (mergedExpected && (mergedExpected.period_kinds ?? []).some((k) => k === 'actual')) {
        merged = {
          expected: mergedExpected,
          high: m(byCase.high ?? byCase.expected) ?? mergedExpected,
          low: m(byCase.low ?? byCase.expected) ?? mergedExpected,
        }
        mergedHistory = true
      }
    }

    const source = merged ?? byCase
    const periods = source.expected!.periods ?? []
    const periodKinds = (source.expected as QuickBooksFpaStatementRender & { period_kinds?: string[] }).period_kinds
      ? ((source.expected as QuickBooksFpaStatementRender & { period_kinds?: string[] }).period_kinds!.map((k) => (k === 'actual' ? 'actual' : 'forecast')) as Array<'actual' | 'forecast'>)
      : derivePeriodKinds(periods, request.anchorPeriod)
    statements.push({
      statementType: type,
      label,
      periods,
      periodKinds,
      rows: rowByCase(source, periods.length, request.hideUnusedRows !== false),
    })
  }
  if (mergedHistory) {
    warnings.push(`Actuals (up to ${historyMonths} months) are shown before the forecast; the split is keyed off the anchor month.`)
  }

  // Seed the live driver model from the expected forecast.
  const plExpected = byTypeCase.get('profit_and_loss:expected') ?? null
  const bsExpected = byTypeCase.get('balance_sheet:expected') ?? null
  const cfExpected = byTypeCase.get('cash_flow:expected') ?? null
  const plStatement = statements.find((s) => s.statementType === 'profit_and_loss')
  const bsStatement = statements.find((s) => s.statementType === 'balance_sheet')
  const cfStatement = statements.find((s) => s.statementType === 'cash_flow')
  const plForecastIdx = plStatement ? firstForecastIndex(plStatement.periodKinds) : 0
  const bsForecastIdx = bsStatement ? firstForecastIndex(bsStatement.periodKinds) : 0
  const cfForecastIdx = cfStatement ? firstForecastIndex(cfStatement.periodKinds) : 0

  // Anchor the driver-model base revenue to a STABLE recent run-rate — the median
  // of the last ~6 ACTUAL months, robust to an abnormal last month — the same
  // `representativeRecentActualValue` the online direct-cash roll-forward uses, so
  // the two anchor identically. The merged P&L carries the real actual revenue
  // history; the raw ML forecast totals are unreliable (it populates leaf accounts,
  // not the aggregate "Total Income" row).
  const revTotalRow = plStatement?.rows.find((r) => (r.isTotal || r.rowType === 'group') && /total income|total revenue|^income$|^revenue$/i.test(r.label))
    ?? plStatement?.rows.find((r) => r.isTotal && /revenue|income|sales/i.test(r.label))
  const seededBaseRevenue = revTotalRow && plStatement
    ? representativeRecentActualValue(revTotalRow.byCase.expected, plStatement.periodKinds, firstForecastIndex(plStatement.periodKinds))
    : null
  const baseMonthlyRevenue =
    (seededBaseRevenue != null && seededBaseRevenue > 0.005 ? seededBaseRevenue : null) ??
    findRowValue(plExpected, /revenue|sales|total income/i, plForecastIdx, { preferTotal: true }) ??
    maxRowValue(plExpected, plForecastIdx) ??
    100000
  // Opening cash = the cash balance at the anchor. Prefer the Balance Sheet cash
  // line just before the first forecast period (the last actual), then the ML
  // cash statement, then sensible fallbacks.
  const openingCash =
    findRowValue(bsExpected, /(total\s+)?(bank|cash and cash|cash)\b/i, Math.max(0, bsForecastIdx - 1), { preferTotal: true }) ??
    findRowValue(cfExpected, /(opening|beginning).*cash|cash.*(begin|opening)/i, cfForecastIdx) ??
    findRowValue(cfExpected, /(ending|closing).*cash/i, Math.max(0, cfForecastIdx - 1)) ??
    findRowValue(bsExpected, /(total\s+)?(bank|cash)\b/i, bsForecastIdx, { preferTotal: true }) ??
    findRowValue(cfExpected, /cash/i, cfForecastIdx) ??
    Math.max(0, baseMonthlyRevenue)
  const horizonMonths = plStatement
    ? Math.max(1, plStatement.periodKinds.filter((k) => k === 'forecast').length || futureMonths)
    : futureMonths

  // The server's monthly direct-cash statement arrives all-null; derive a
  // reconciling indirect cash flow from the populated P&L + Balance Sheet so the
  // Cash Flow / Forecast Range / Accuracy sheets render real numbers that tie to
  // the Balance Sheet. When the page supplies the online direct-cash forecast,
  // its (anchored, tight) ending-cash bands drive the forecast periods so the
  // export matches online. Falls back to whatever cash statement we already have.
  const directEndingByCase = extractDirectEndingByCase(request.forecastCashByCase)
  if (directEndingByCase) warnings.push('Forecast cash bands use the live direct-cash model (matching the online forecast).')
  const derivedCash = deriveCashFlowStatement(plStatement, bsStatement, openingCash, directEndingByCase, request.forecastCashByCase)
  if (derivedCash) {
    const idx = statements.findIndex((s) => s.statementType === 'cash_flow')
    if (idx >= 0) statements[idx] = derivedCash
    else statements.push(derivedCash)
    warnings.push('Cash Flow is derived from the forecast P&L and Balance Sheet (ending cash ties to the Balance Sheet cash line).')
  }

  // Sub-monthly cash (13-week / daily). Prefer the page-harvested TIMING-AWARE direct
  // cash (dated AR/AP + recurring payroll/rent/loan schedules); then the server render;
  // then an even allocation of the monthly cash. The server serves the period axis but
  // values usually arrive null, so the even spread is the final fallback (e.g. headless).
  const cashForSub = statements.find((s) => s.statementType === 'cash_flow') ?? derivedCash ?? null
  const subCashHasValues = (s: CfoStatementData | null): boolean =>
    !!s && s.rows.some((r) => CASES.some((c) => r.byCase[c].some((v) => typeof v === 'number' && Number.isFinite(v))))
  const weeklyHarvested = statementDataFromHarvestedCashByCase(request.weeklyCashByCase, '13-Week Cash', request.anchorPeriod)
  const dailyHarvested = statementDataFromHarvestedCashByCase(request.dailyCashByCase, 'Daily Cash', request.anchorPeriod)
  const [weeklyRaw, dailyRaw] = await Promise.all([
    fetchGrainCash(request, 'weekly', '13-Week Cash'),
    fetchGrainCash(request, 'daily', 'Daily Cash'),
  ])
  const weeklyCash = (weeklyHarvested && subCashHasValues(weeklyHarvested) ? weeklyHarvested : null)
    ?? (subCashHasValues(weeklyRaw) ? weeklyRaw : null)
    ?? allocateSubMonthlyCash(cashForSub, weeklyRaw, openingCash)
  const dailyCash = (dailyHarvested && subCashHasValues(dailyHarvested) ? dailyHarvested : null)
    ?? (subCashHasValues(dailyRaw) ? dailyRaw : null)
    ?? allocateSubMonthlyCash(cashForSub, dailyRaw, openingCash)
  const subCashIsTimingAware = (weeklyHarvested && subCashHasValues(weeklyHarvested)) || (dailyHarvested && subCashHasValues(dailyHarvested))
  if (weeklyCash || dailyCash) {
    warnings.push(subCashIsTimingAware
      ? 'Daily/weekly cash uses the timing-aware direct-cash model (dated AR/AP and recurring payroll/rent/loan schedules), matching the online liquidity views. Driver edits apply at monthly grain, so under non-default drivers the 13-week sum can differ slightly from the monthly.'
      : 'Daily/weekly cash is allocated evenly from the monthly forecast (liquidity views).')
  }

  if (statements.length === 0) {
    warnings.push('No forecast statements could be loaded for this workbook.')
  }
  warnings.push('High / Expected / Low are ML prediction bands, not independent scenarios.')
  warnings.push('The Drivers sheet is a simplified what-if model seeded to the ML expected forecast.')

  const driverProvenance = buildDriverProvenance(actualPl, actualBs)
  const closedMonths = request.closedMonthsCount ?? closedActualMonthCount(actualPl)
  const backtest = request.forecastVsActualRows && request.forecastVsActualRows.length
    ? {
      rows: request.forecastVsActualRows,
      readinessStatus: request.readinessStatus ?? null,
      basisLabels: request.basisLabels ?? null,
      recentMonths: 3,
    }
    : null
  const vintage = {
    dataAsOf: request.dataAsOf ?? request.anchorPeriod ?? null,
    anchorMonth: request.anchorPeriod ?? null,
    packageId: request.packageId ?? null,
    readinessStatus: request.readinessStatus ?? null,
    closedMonthsCount: closedMonths > 0 ? closedMonths : null,
  }

  return {
    companyName: request.companyName,
    anchorPeriod: request.anchorPeriod,
    grain: 'month',
    grainLabel: 'Monthly',
    currencySymbol: request.currencySymbol ?? '$',
    generatedAtIso: request.generatedAtIso,
    version: 'v1.0',
    statements,
    weeklyCash,
    dailyCash,
    drivers: normalizeDrivers(request.assumptions, request.baselineAssumptions, driverProvenance),
    seed: { baseMonthlyRevenue: Math.abs(baseMonthlyRevenue), openingCash, horizonMonths },
    backtest,
    vintage,
    warnings,
  }
}
